import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Coffee } from "lucide-react";
import { CAFE } from "@/constants/testIds";
import { formatMoney } from "@/lib/currency";
import { toast } from "sonner";

const COLUMNS = [
  { key: "new", label: "New", next: "preparing" },
  { key: "preparing", label: "Preparing", next: "ready" },
  { key: "ready", label: "Ready", next: "served" },
  { key: "served", label: "Served" },
];

export default function KitchenBoard() {
  const [orders, setOrders] = useState([]);

  const load = async () => {
    const { data } = await api.get("/cafe/orders");
    setOrders(data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  const advance = async (order, nextStatus) => {
    try {
      await api.patch(`/cafe/orders/${order.id}/status`, { status: nextStatus });
      toast.success(`Order #${order.id.slice(-4).toUpperCase()} → ${nextStatus}`);
      await load();
    } catch {
      toast.error("Could not update order.");
    }
  };

  const grouped = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
  for (const o of orders) grouped[o.status]?.push(o);

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Café</div>
        <h1 className="font-display text-4xl">Kitchen board</h1>
        <p className="text-muted-foreground text-sm mt-1">Live order flow · auto-refresh every 15s</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {COLUMNS.map((col) => (
          <div key={col.key} className="rounded-xl border border-border bg-secondary/30">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="text-[11px] uppercase tracking-[0.22em]">{col.label}</div>
              <span className="text-xs text-muted-foreground">{grouped[col.key].length}</span>
            </div>
            <div className="p-3 space-y-2 min-h-[200px]">
              {grouped[col.key].length === 0 && (
                <div className="text-xs text-muted-foreground py-6 text-center">No orders.</div>
              )}
              {grouped[col.key].map((o) => (
                <div
                  key={o.id}
                  data-testid={CAFE.orderCard(o.id)}
                  className="rounded-lg bg-card border border-border p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-mono text-xs text-muted-foreground">#{o.id.slice(-4).toUpperCase()}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {o.order_type}{o.table_number ? ` · T${o.table_number}` : ""}
                    </div>
                  </div>
                  <ul className="text-sm space-y-0.5 mb-2">
                    {o.lines?.map((l, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">{l.quantity}× {l.name}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/60">
                    <div className="font-display text-lg">{formatMoney(o.total)}</div>
                    {col.next && (
                      <Button
                        data-testid={CAFE.orderAdvance(o.id)}
                        size="sm"
                        onClick={() => advance(o, col.next)}
                        className="rounded-full text-xs h-7"
                      >
                        → {col.next}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
